/**
 * Workspace GitHub Integration API Client
 * GitHub App installation management, repo browsing, branch listing.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

const WORKSPACES_API_BASE = '/api/workspaces';

async function request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(endpoint, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...options.headers },
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error(await response.text() || response.statusText);
  }
  if (response.status === 204) return null as T;
  return response.json();
}

// ─── Types ──────────────────────────────────────────────────────

export interface GitHubStatus {
  connected: boolean;
  appConfigured: boolean;
  installationId?: number;
  orgLogin?: string;
  orgAvatarUrl?: string;
  installedAt?: string;
}

export interface GitHubRepo {
  id: number;
  name: string;
  full_name: string;
  private: boolean;
  html_url: string;
  description: string | null;
  default_branch: string;
  language: string | null;
  updated_at: string;
}

export interface GitHubBranch {
  name: string;
  protected: boolean;
}

// ─── API Functions ──────────────────────────────────────────────

export async function getGitHubStatus(workspaceId: string): Promise<GitHubStatus> {
  return request<GitHubStatus>(`${WORKSPACES_API_BASE}/${workspaceId}/github/status`);
}

export async function installGitHub(
  workspaceId: string,
  installationId: number,
): Promise<{ connected: boolean }> {
  return request(`${WORKSPACES_API_BASE}/${workspaceId}/github/install`, {
    method: 'POST',
    body: JSON.stringify({ installation_id: installationId }),
  });
}

export async function uninstallGitHub(
  workspaceId: string,
): Promise<{ connected: boolean }> {
  return request(`${WORKSPACES_API_BASE}/${workspaceId}/github/uninstall`, {
    method: 'POST',
  });
}

export async function listGitHubRepos(
  workspaceId: string,
  options?: { page?: number; perPage?: number; search?: string },
): Promise<{ repos: GitHubRepo[]; totalCount: number }> {
  const params = new URLSearchParams();
  if (options?.page) params.set('page', String(options.page));
  if (options?.perPage) params.set('per_page', String(options.perPage));
  if (options?.search) params.set('search', options.search);
  const qs = params.toString();
  return request(`${WORKSPACES_API_BASE}/${workspaceId}/github/repos${qs ? `?${qs}` : ''}`);
}

export async function listGitHubBranches(
  workspaceId: string,
  owner: string,
  repo: string,
): Promise<{ branches: GitHubBranch[] }> {
  return request(`${WORKSPACES_API_BASE}/${workspaceId}/github/repos/${owner}/${repo}/branches`);
}

export async function createGitHubRepo(
  workspaceId: string,
  options: { name: string; description?: string; isPrivate?: boolean },
): Promise<GitHubRepo> {
  return request<GitHubRepo>(`${WORKSPACES_API_BASE}/${workspaceId}/github/repos`, {
    method: 'POST',
    body: JSON.stringify(options),
  });
}

// ─── Hooks ──────────────────────────────────────────────────────

export function useGitHubStatus(workspaceId: string | undefined) {
  return useQuery({
    queryKey: ['github-status', workspaceId],
    queryFn: () => getGitHubStatus(workspaceId!),
    enabled: !!workspaceId,
    staleTime: 60000,
  });
}

export function useGitHubRepos(workspaceId: string | undefined, options?: { page?: number; search?: string }) {
  return useQuery({
    queryKey: ['github-repos', workspaceId, options?.page, options?.search],
    queryFn: () => listGitHubRepos(workspaceId!, { page: options?.page, search: options?.search }),
    enabled: !!workspaceId,
    staleTime: 30000,
  });
}

export function useGitHubBranches(workspaceId: string | undefined, owner: string, repo: string) {
  return useQuery({
    queryKey: ['github-branches', workspaceId, owner, repo],
    queryFn: () => listGitHubBranches(workspaceId!, owner, repo),
    enabled: !!(workspaceId && owner && repo),
    staleTime: 30000,
  });
}

export function useInstallGitHub() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ workspaceId, installationId }: { workspaceId: string; installationId: number }) =>
      installGitHub(workspaceId, installationId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['github-status', variables.workspaceId] });
    },
  });
}

export function useCreateGitHubRepo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ workspaceId, name, description, isPrivate }: {
      workspaceId: string; name: string; description?: string; isPrivate?: boolean;
    }) => createGitHubRepo(workspaceId, { name, description, isPrivate }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['github-repos', variables.workspaceId] });
    },
  });
}

export function useUninstallGitHub() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ workspaceId }: { workspaceId: string }) =>
      uninstallGitHub(workspaceId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['github-status', variables.workspaceId] });
      queryClient.invalidateQueries({ queryKey: ['github-repos', variables.workspaceId] });
    },
  });
}
